/**
 * A client's confirmed document figures by tax year (each W-2, 1099, 1098... with its boxes), and
 * how many uploads still wait in Review › Documents. The start of the tax input sheet.
 */
import { taxDocType, type DocOwner } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { Empty, Loading } from "@/components/page";
import { OWNER_LABELS } from "@/pages/staff/document-review";

type Figure = { id: string; documentId: string; filename: string; docType: string; taxYear: number | null; issuer: string | null; whose: DocOwner | null; source: "ai" | "manual"; confirmedBy: string | null; confirmedAt: string; fields: Record<string, string> };

export function DocumentFigures({ clientId }: { clientId: string }) {
  const { data } = useQuery<Figure[]>({ queryKey: [`/api/clients/${clientId}/document-figures`] });
  const { data: waiting } = useQuery<{ counts: Record<string, number> }>({ queryKey: [`/api/document-reads?status=review&clientId=${clientId}`] });
  const years = Array.from(new Set((data ?? []).map((f) => f.taxYear ?? 0))).sort((a, b) => b - a);
  const [year, setYear] = useState<number | null>(null);
  const shown = year ?? years[0] ?? null;
  const n = waiting?.counts.review ?? 0;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      {n > 0 && (
        <Link href={`/documents/review?clientId=${clientId}`} className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm hover:bg-amber-100 dark:border-amber-700/50 dark:bg-amber-950/30">
          <span>
            {n} {n === 1 ? "document waits" : "documents wait"} for review
          </span>
          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
        </Link>
      )}
      {data.length === 0 ? (
        <Empty>No confirmed figures yet. They appear here as uploads are confirmed in Review › Documents.</Empty>
      ) : (
        <>
          {years.length > 1 && (
            <div role="tablist" aria-label="Tax year" className="flex flex-wrap gap-1">
              {years.map((y) => (
                <button key={y} role="tab" aria-selected={shown === y} onClick={() => setYear(y)} className={`rounded-md px-2.5 py-1 text-sm ${shown === y ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>
                  {y || "No year"}
                </button>
              ))}
            </div>
          )}
          <ul className="space-y-3">
            {data
              .filter((f) => (f.taxYear ?? 0) === shown)
              .map((f) => {
                const type = taxDocType(f.docType);
                const boxes = type.fields.filter((x) => f.fields[x.key]);
                return (
                  <li key={f.id} className="min-w-0 rounded-md border border-border p-3">
                    <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-sm font-medium">
                        {type.label}
                        {f.issuer ? <span className="font-normal text-muted-foreground"> · {f.issuer}</span> : null}
                      </p>
                      <Link href={`/documents/review?id=${f.id}&tab=confirmed`} className="text-xs text-primary hover:underline">
                        {f.filename}
                      </Link>
                    </div>
                    {boxes.length > 0 && (
                      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                        {boxes.map((b) => (
                          <div key={b.key} className="flex min-w-0 justify-between gap-3 border-b border-dashed border-border/70 py-0.5">
                            <dt className="min-w-0 truncate text-muted-foreground" title={b.label}>
                              {b.label}
                            </dt>
                            <dd className={b.kind === "money" ? "tabular shrink-0" : "min-w-0 truncate"}>{f.fields[b.key]}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {f.whose ? `${OWNER_LABELS[f.whose]} · ` : ""}
                      {f.source === "ai" ? "Read by AI, " : "Entered, "}confirmed{f.confirmedBy ? ` by ${f.confirmedBy}` : ""}
                    </p>
                  </li>
                );
              })}
          </ul>
        </>
      )}
      <p className="text-xs text-muted-foreground">Figures are as printed on each document and confirmed by your team.</p>
    </div>
  );
}
